import { memo, Suspense, useCallback } from 'react';

import { useTransition } from '@react-spring/web';

import { type ModalName } from '@/modals-names';

import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';
import { ModalContextProvider } from '@/shared/ui/modal';

import { useModalsActions, useModalState } from '../modals-store';
import { type ModalRegistration } from '../types';

interface Props {
  name: ModalName;
  Component: ModalRegistration['Component'];
}

export const ModalWrapper = memo<Props>(({ name, Component }) => {
  const { isOpen = false, props } = useModalState(name);
  const { close: closeModal } = useModalsActions();

  const close = useCallback(() => {
    closeModal(name);
  }, [closeModal, name]);

  const isSlowDevice = getIsSlowDevice();

  const transitions = useTransition(isOpen, {
    from: { translateY: '100%', scale: 0.97, backdrop: 0 },
    enter: { translateY: '0%', scale: 1, backdrop: 0.4 },
    leave: { translateY: '100%', scale: 0.97, backdrop: 0 },
    immediate: isSlowDevice,
    config: (isOpen, _, phase) => {
      return {
        tension: 230,
        friction: 20,
        // Disable spring for close animation
        clamp: !isOpen || phase === 'leave',
      };
    },
  });

  return transitions(
    (style, shouldRender, transition) =>
      shouldRender && (
        <Suspense fallback={null}>
          <ModalContextProvider name={name} style={style} transition={transition}>
            <Component
              {...{
                ...props,
                close,
              }}
            />
          </ModalContextProvider>
        </Suspense>
      )
  );
});
